import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addBookmark, fetchBookmarks, removeBookmark } from '../api/bookmarksClient'
import { toPost } from '../../posts/utils/postsUtils'
import type { Post } from '../../posts/model/contracts'

const BOOKMARKS_KEY = ['bookmarks'] as const

type UseBookmarksOptions = {
  limit?: number
  offset?: number
}

export function useBookmarks(options: UseBookmarksOptions = {}) {
  const { limit = 20, offset = 0 } = options
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [...BOOKMARKS_KEY, { limit, offset }],
    queryFn: () => fetchBookmarks({ limit, offset }),
    select: (data): Post[] => data.items.map(toPost),
  })

  const addMutation = useMutation({
    mutationFn: (postId: string) => addBookmark(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOOKMARKS_KEY })
    },
  })

  const removeMutation = useMutation({
    mutationFn: (postId: string) => removeBookmark(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BOOKMARKS_KEY })
    },
  })

  const save = useCallback((postId: string) => addMutation.mutate(postId), [addMutation])
  const unsave = useCallback(
    (postId: string) => removeMutation.mutate(postId),
    [removeMutation],
  )

  return {
    posts: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    save,
    unsave,
    isSaving: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  }
}
